import { ArrowRight, LockKeyhole, Scale } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

export default function LoginPage() {
  const [mode, setMode] = useState("login");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestedReturnTo = searchParams.get("returnTo");
  const returnTo = requestedReturnTo?.startsWith("/pilot/claim")
    ? requestedReturnTo
    : "/";

  const submit = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = {
      email: form.get("email"),
      password: form.get("password"),
    };
    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/${
          mode === "login" ? "login" : "register"
        }`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Sign-in failed");
      navigate(returnTo, { replace: true });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const signInWithGoogle = () => {
    const redirectUrl = window.location.origin + returnTo;
    const target = encodeURIComponent(redirectUrl);
    window.location.href = `https://auth.emergentagent.com/?redirect=${target}`;
  };

  return (
    <main className="login-page" data-testid="login-page">
      <section className="login-panel">
        <div className="login-brand">
          <span className="brand-mark">
            <Scale size={20} />
          </span>
          <span>PFJudgePro</span>
        </div>
        <p className="eyebrow">Judge workspace</p>
        <h1>{mode === "login" ? "Welcome back." : "Start judging smarter."}</h1>
        <p className="login-copy">
          Keep your debate work and Pro access tied to your own account.
        </p>
        <button
          className="google-action"
          onClick={signInWithGoogle}
          data-testid="google-sign-in-button"
        >
          <span className="google-g">G</span>Continue with Google
        </button>
        <div className="auth-divider">
          <span>or</span>
        </div>
        <form
          onSubmit={submit}
          className="auth-form"
          data-testid="email-auth-form"
        >
          <label>
            Email
            <input
              name="email"
              type="email"
              required
              data-testid="auth-email-input"
            />
          </label>
          <Link to="/forgot-password" data-testid="forgot-password-link">
            Forgot password?
          </Link>
          <label>
            Password
            <input
              name="password"
              type="password"
              required
              minLength="8"
              data-testid="auth-password-input"
            />
          </label>
          <button
            className="primary-action"
            disabled={isSubmitting}
            data-testid="email-auth-submit-button"
          >
            <LockKeyhole size={16} />
            {isSubmitting
              ? "Please wait"
              : mode === "login"
                ? "Sign in"
                : "Create account"}
          </button>
        </form>
        <button
          className="auth-switch"
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          data-testid="auth-mode-switch-button"
        >
          {mode === "login"
            ? "New to PFJudgePro? Create an account"
            : "Already have an account? Sign in"}
          <ArrowRight size={15} />
        </button>
      </section>
      <aside className="login-aside">
        <p>
          Clear flow.
          <br />
          <em>Confident calls.</em>
        </p>
        <span>Public Forum judging tools</span>
      </aside>
    </main>
  );
}
